import { formatCurrency } from '../../lib/CurrencyHelpers.js';
import { formatDateTime, formatDateTimeLong } from '../../lib/DateTime.js';
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { sumProperty, ucfirst } from '../../lib/Utils.js';
import { useState } from 'react';
import Modal from '../Modal';
import EditManualPayment from './EditManualPayment.jsx';
import CertificateIcon from '~/components/icons/CertificateIcon';
import { couponHumanDiscount } from '~/lib/CouponHelpers';
import GiftCertificatePaymentLine from '~/components/giftcertificates/GiftCertificatePaymentLine';
import useBusinessPostApi from '~/lib/useBusinessPostApi';

const ItemRow = ({ icon, title, description, amount, isFirst }) => {
    return (
        <div className="flex">
            <div className="w-6 h-6 mr-2 py-3">{icon}</div>
            <div
                className={`flex py-3 pr-4 justify-between items-center flex-1 ${!isFirst ? 'border-t' : ''}`}
            >
                <div>
                    {title}
                    <div className="text-sm text-gray-500">{description}</div>
                </div>
                {!isNaN(parseFloat(amount)) && (
                    <div className="font-medium ml-4">
                        {formatCurrency(amount)}
                    </div>
                )}
            </div>
        </div>
    );
};

const PaymentHistoryRow = ({
    payment,
    isFirst,
    isLast,
    onUpdate,
    renderDetails,
    refetch,
}) => {
    const processorName = ucfirst(payment.processor);
    const isFailed = !!payment?.is_failed;

    return (
        <ItemRow
            icon={
                isFailed ? (
                    <XCircleIcon className="text-red-600" />
                ) : (
                    <CheckCircleIcon className="text-green-500" />
                )
            }
            title={isFailed ? 'Payment Failed' : 'Payment'}
            isFirst={isFirst}
            amount={processorName === 'Skip' ? 0 : payment.amount}
            description={
                <div>
                    <div>{formatDateTimeLong(payment.created_at)}</div>
                    {!!payment.external_link && (
                        <div>
                            via{' '}
                            <a
                                href={payment.external_link}
                                target="_blank"
                                className="text-gray-500"
                            >
                                {processorName}
                            </a>
                        </div>
                    )}
                    {!payment.external_link && !isFailed && (
                        <div>
                            {processorName === 'Skip' &&
                                'Skipped paying online'}
                            {processorName === 'Giftcertificate' && (
                                <GiftCertificatePaymentLine payment={payment} />
                            )}
                            {processorName !== 'Skip' &&
                                processorName !== 'Giftcertificate' &&
                                `via ${processorName}`}
                            {!!payment.external_id &&
                                processorName !== 'Giftcertificate' &&
                                ` (${payment.external_id})`}
                            {payment.processor === 'manual' && (
                                <EditManualPayment
                                    payment={payment}
                                    onUpdate={onUpdate}
                                />
                            )}
                        </div>
                    )}
                    {!!payment.settings?.notes && (
                        <div className="mb-2">{payment.settings?.notes}</div>
                    )}

                    {!!renderDetails &&
                        renderDetails({
                            payment,
                            isFirst,
                            isLast,
                            refetchPayments: refetch,
                        })}
                </div>
            }
        />
    );
};

const PaymentHistory = ({
    totalPrice,
    history,
    codes,
    savedPaymentMethods,
    onUpdate,
    onRemoveCoupons,
    isRemovingCoupons,
    renderDetails,
    refetch,
}) => {
    const totalPaid = sumProperty(history, 'amount');
    const amountDue = Math.max(0, totalPrice - totalPaid);

    return (
        <div className="panel pl-4">
            {history?.map &&
                history.map((payment, idx) => (
                    <PaymentHistoryRow
                        payment={payment}
                        key={payment.id}
                        isFirst={idx === 0}
                        isLast={idx === history.length - 1}
                        onUpdate={onUpdate}
                        renderDetails={renderDetails}
                        refetch={refetch}
                    />
                ))}
            {codes?.map(code => (
                <ItemRow
                    key={code?.id}
                    icon={<CertificateIcon className="text-[#D37DE8] mt-1" />}
                    title={code?.voucher?.name || 'Unknown Code'}
                    amount={null}
                    description={
                        <div>
                            Code {code?.code} (
                            {couponHumanDiscount(code?.voucher)} off)
                            {typeof onRemoveCoupons === 'function' && (
                                <>
                                    <span className="mx-1">&middot;</span>
                                    <button
                                        type="submit"
                                        className="link-no-underline"
                                        onClick={() => {
                                            onRemoveCoupons();
                                        }}
                                    >
                                        {isRemovingCoupons
                                            ? 'Removing...'
                                            : 'Remove'}
                                    </button>
                                </>
                            )}
                        </div>
                    }
                    isFirst={false}
                />
            ))}
            {!history?.length && !!savedPaymentMethods?.length && (
                <ItemRow
                    icon={<InformationCircleIcon className="text-green-500" />}
                    title="Card on File"
                    amount={null}
                    description={`A verified payment method has been saved for this`}
                    isFirst={!history?.length}
                />
            )}
            {amountDue > 0 && (
                <ItemRow
                    icon={<ExclamationCircleIcon className="text-red-600" />}
                    title="Balance Due"
                    amount={amountDue}
                    description={`Total amount is ${formatCurrency(totalPrice)}, but ${formatCurrency(totalPaid)} has been paid`}
                    isFirst={!history?.length}
                />
            )}
            {amountDue <= 0 && totalPaid > 0 && (
                <ItemRow
                    icon={<CheckCircleIcon className="text-green-500" />}
                    title="Fully Paid"
                    amount={null}
                    isFirst={!history?.length}
                    description={
                        totalPrice > 0
                            ? `Total amount is ${formatCurrency(totalPrice)}, amount paid is ${formatCurrency(totalPaid)}`
                            : `Total amount paid is ${formatCurrency(totalPaid)}`
                    }
                />
            )}
        </div>
    );
};

export default PaymentHistory;
